/**
 * Reservation View — Table booking form (extracted from original restaurant.ts)
 * Renders: meal period, seating area, date/time picker, party size, dietary input.
 * All business logic, handlers, and Litepicker init unchanged from original.
 */

import { AppVersionFooter, FloatingNav, BackToTopButton, initBackToTop } from '../../components';
import { setState, navigateTo } from '../../store';
import { getRestaurantConfig } from '../../api';
import { getCurrentLanguage, formatLocalDate, t } from '../../i18n';
import { renderCarousel } from '../../components/carousel';
import { loadMenuForOutlet } from './menu-view';
import Litepicker from 'litepicker';
import 'litepicker/dist/plugins/mobilefriendly';

// ── Local interfaces (kept as-is from original) ──────────────────────────────
interface LocalizedStr { th: string; en: string; zh: string; ru?: string; ja?: string; ko?: string; }
interface MealPeriod { id: string; name: LocalizedStr | string; slots: string[]; timeSlots?: string[]; maxPaxPerSlot?: number; }
interface OutletImage { url: string; thumbUrl?: string; }
interface SeatingArea { id: string; name: LocalizedStr | string; }
export interface RestaurantOutletData {
  id?: string; name?: LocalizedStr | string; description?: LocalizedStr | string;
  enabled?: boolean; openTime?: string; closeTime?: string;
  images?: OutletImage[]; mealPeriods?: MealPeriod[]; seatingAreas?: SeatingArea[];
  outlets?: RestaurantOutletData[];
}

// ── Shared exported state (read by index.ts for cover image / outlet name) ──
export let restaurantOutlets: RestaurantOutletData[] = [];
export let activeOutletId: string | null = null;
export let restConfigLoaded = false;

let MEAL_PERIODS: MealPeriod[] = [
  { id: 'breakfast', name: { th: 'อาหารเช้า (Breakfast)', en: 'Breakfast', zh: '早餐', ru: 'Завтрак', ja: '朝食', ko: '조식' }, slots: ['07:00', '08:00', '09:00', '10:00'] },
  { id: 'lunch', name: { th: 'อาหารกลางวัน (Lunch)', en: 'Lunch', zh: '午餐', ru: 'Обед', ja: '昼食', ko: '중식' }, slots: ['11:30', '12:00', '12:30', '13:00', '13:30'] },
  { id: 'dinner', name: { th: 'อาหารเย็น (Dinner)', en: 'Dinner', zh: '晚餐', ru: 'Ужин', ja: '夕食', ko: '석식' }, slots: ['18:00', '18:30', '19:00', '19:30', '20:00'] },
];
const SEATING_AREAS: SeatingArea[] = [
  { id: 'Indoor (แอร์)', name: { th: 'Indoor (แอร์)', en: 'Indoor (A/C)', zh: '室内 (空调)', ru: 'В помещении (кондиционер)', ja: '屋内 (エアコン完備)', ko: '실내 (에어컨)' } },
  { id: 'Outdoor (ริมสระ)', name: { th: 'Outdoor (ริมสระ)', en: 'Outdoor (Poolside)', zh: '室外 (泳池边)', ru: 'На открытом воздухе (у бассейна)', ja: '屋外 (プールサイド)', ko: '야외 (수영장 옆)' } },
  { id: 'VIP Room', name: { th: 'VIP Room', en: 'VIP Room', zh: 'VIP 贵宾房', ru: 'VIP-зал', ja: 'VIP個室', ko: 'VIP 룸' } },
];

let selectedMealId = 'dinner';
let selectedSeating = 'Outdoor (ริมสระ)';
let selectedDateStr = formatLocalDate();
let selectedTimeSlot = '19:00';
let partySizeCount = 4;
let dietaryInput = '';
let roomNumberInput = '';

// ── Helpers ─────────────────────────────────────────────────────────────────
export function getLocalizedText(val: any, lang: string, fallback = ''): string {
  if (!val) return fallback;
  if (typeof val === 'string') return val;
  if (typeof val === 'object') return val[lang] || val.en || val.th || val.zh || val.ru || val.ja || val.ko || fallback;
  return fallback;
}

function matchSeatingAreaId(areas: SeatingArea[], target: any): string | null {
  if (!target) return null;
  const trimLC = (s: string) => s.trim().toLowerCase();
  const found = areas.find((s) => {
    if (typeof target === 'string') {
      const t2 = trimLC(target);
      return s.id === target || (typeof s.name === 'string' && trimLC(s.name) === t2) ||
        (typeof s.name === 'object' && Object.values(s.name).some((v) => v && trimLC(v as string) === t2));
    }
    if (typeof target === 'object') {
      if (target.id && s.id === target.id) return true;
      if (typeof s.name === 'object') return Object.keys(target).some((k) => target[k] && (s.name as any)[k] && trimLC(target[k]) === trimLC((s.name as any)[k]));
    }
    return false;
  });
  return found?.id ?? null;
}

/** Getters for index.ts to read current outlet data without re-importing state */
export function getActiveOutlet(): RestaurantOutletData {
  return restaurantOutlets.find((o) => o.id === activeOutletId) || restaurantOutlets[0] || {};
}

// ── Render (HTML body for restaurant page) ─────────────────────────────────
export function renderReservationBody(modeSwitcher: string, mode: 'reserve' | 'order' = 'reserve', menuHtml = ''): string {
  const lang = getCurrentLanguage();
  const activeOutlet = getActiveOutlet();
  const currentMealPeriods = activeOutlet.mealPeriods || MEAL_PERIODS;
  const currentSeatingAreas = (activeOutlet.seatingAreas?.length ?? 0) > 0 ? activeOutlet.seatingAreas! : SEATING_AREAS;
  const currentMeal = currentMealPeriods.find((m) => m.id === selectedMealId) || currentMealPeriods[0] || MEAL_PERIODS[2];

  const editDataStr = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('editBookingData') : null;
  if (editDataStr) { try { applyEditData(JSON.parse(editDataStr), currentSeatingAreas); } catch (_) {} }

  const matchedSeat = matchSeatingAreaId(currentSeatingAreas, selectedSeating);
  selectedSeating = matchedSeat || currentSeatingAreas[0]?.id || selectedSeating;

  const ui = getUiText(lang);
  const openHoursText = activeOutlet.openTime && activeOutlet.closeTime
    ? `${activeOutlet.openTime} – ${activeOutlet.closeTime}` : '06:30 – 22:30';

  const multiOutletTabs = `
    <div id="rest-outlets-scroll" style="display:flex;gap:8px;overflow-x:auto;-webkit-overflow-scrolling:touch;padding-bottom:8px;margin-bottom:12px;scrollbar-width:none;-ms-overflow-style:none;">
      ${(restaurantOutlets.length > 0 ? restaurantOutlets : [{ id: activeOutletId || 'restaurant', name: activeOutlet.name }]).map((o) => {
        const isSelected = !activeOutletId || activeOutletId === o.id || restaurantOutlets.length === 1;
        return `
        <button id="rest-outlet-tab-${o.id}" type="button" ${restaurantOutlets.length > 1 ? `onclick="window.selectRestOutlet?.('${o.id}')"` : ''}
          style="flex:0 0 auto;padding:8px 16px;border-radius:20px;border:1.5px solid ${isSelected ? '#00B900' : '#e0e0e0'};background:${isSelected ? '#00B900' : '#fff'};color:${isSelected ? '#fff' : '#333'};font-size:13px;font-weight:600;white-space:nowrap;cursor:${restaurantOutlets.length > 1 ? 'pointer' : 'default'};transition:all 0.15s ease;">
          🍽️ ${getLocalizedText(o.name, lang, o.id || 'Restaurant')}
        </button>`;
      }).join('')}
    </div>`;

  const editBanner = (() => {
    const editId = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('editBookingId') : null;
    if (!editId) return '';
    const editData = editDataStr ? JSON.parse(editDataStr) : {};
    return `<div style="margin-bottom:12px;padding:10px 14px;background:#fff3cd;border-left:4px solid #ffc107;border-radius:6px;font-size:13px;color:#856404;display:flex;justify-content:space-between;align-items:center;">
      <div><strong>✏️ ${t('editModeBanner')}</strong> (${editData.confirmationNumber || editId})</div>
      <button onclick="sessionStorage.removeItem('editBookingId');sessionStorage.removeItem('editBookingData');window.location.reload();" style="border:none;background:none;color:#dc3545;font-size:12px;cursor:pointer;text-decoration:underline;">${t('cancelEdit')}</button>
    </div>`;
  })();

  const isReserve = mode === 'reserve';

  return `
    ${editBanner}
    ${multiOutletTabs}
    <div class="service-detail-layout">
      <div class="service-left-col">
        <!-- Restaurant Info & Cover Image Card -->
        <div style="background:#fff;border-radius:14px;overflow:hidden;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);padding:0;">
          <div style="padding:8px 8px 0 8px;">
            <div class="room-images" id="rest-images-container" style="position:relative;border-radius:10px;overflow:hidden;height:220px;margin:0;">
              <div style="width:100%;height:220px;background:linear-gradient(135deg,#00B900,#008a00);display:flex;align-items:center;justify-content:center;color:#fff;font-size:36px;">🍽️</div>
            </div>
          </div>
          <div style="padding:12px 14px 14px 14px;">
            <h2 style="font-size:18px;font-weight:700;color:#1a1a1a;margin-bottom:4px;">${getLocalizedText(activeOutlet.name, lang, 'ร้านอาหาร (Restaurant)')}</h2>
            ${activeOutlet.description ? `<p style="font-size:13px;color:#666;line-height:1.4;margin-bottom:10px;">${getLocalizedText(activeOutlet.description, lang, '')}</p>` : ''}
            <span style="display:inline-flex;align-items:center;padding:4px 10px;background:#E8F5E8;color:#00B900;border-radius:12px;font-size:12px;font-weight:600;">${ui.openHours}: ${openHoursText}</span>
          </div>
        </div>

        <!-- Mode Toggle: Placed before Select Meal Period -->
        ${modeSwitcher}

        ${isReserve ? `
        <!-- Meal Period -->
        <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
          <h3 style="font-size:16px;font-weight:600;margin-bottom:12px;color:#1a1a1a;">${ui.mealTitle}</h3>
          <div style="display:flex;gap:8px;">
            ${currentMealPeriods.map((m) => {
              const mName = getLocalizedText(m.name, lang, m.id);
              const isOn = selectedMealId === m.id;
              return `<button id="rest-meal-btn-${m.id}" class="rest-meal-btn" onclick="window.selectMealPeriod?.('${m.id}')"
                style="flex:1;padding:10px 6px;border-radius:8px;border:1px solid ${isOn ? '#00B900' : '#e0e0e0'};background:${isOn ? '#E8F5E8' : '#fff'};color:${isOn ? '#00B900' : '#333'};font-size:13px;font-weight:${isOn ? '600' : '400'};cursor:pointer;"
              >${mName.split(' ')[0]}</button>`;
            }).join('')}
          </div>
        </div>

        <!-- Seating Area -->
        <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
          <h3 style="font-size:16px;font-weight:600;margin-bottom:12px;color:#1a1a1a;">${ui.seatingTitle}</h3>
          <div style="display:flex;gap:8px;">
            ${currentSeatingAreas.map((s) => {
              const aName = getLocalizedText(s.name, lang, s.id);
              const isOn = selectedSeating === s.id;
              return `<button id="rest-seat-btn-${s.id.replace(/[^a-zA-Z0-9]/g,'_')}" class="rest-seat-btn" onclick="window.selectSeatingArea?.('${s.id}')"
                style="flex:1;padding:10px 6px;border-radius:8px;border:1.5px solid ${isOn ? '#00B900' : '#e0e0e0'};background:${isOn ? '#F9FDF9' : '#fff'};color:${isOn ? '#00B900' : '#333'};font-size:13px;font-weight:${isOn ? '600' : '400'};cursor:pointer;"
              >${aName}</button>`;
            }).join('')}
          </div>
        </div>
        ` : `
        <!-- Food Menu Section (Order Mode) -->
        <div id="rest-menu-container" style="margin-top:16px;">
          ${menuHtml}
        </div>
        `}
      </div>

      ${isReserve ? `
      <div class="service-right-col">
        <!-- Date & Time -->
        <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
          <h3 style="font-size:16px;font-weight:600;margin-bottom:12px;color:#1a1a1a;">${ui.dateTitle}</h3>
          <div class="date-section-vertical" style="margin-bottom:12px;">
            <div id="rest-date-range-trigger" class="date-range-display" style="cursor:pointer;display:flex;align-items:center;justify-content:space-between;padding:12px 14px;border:1.5px solid #e0e0e0;border-radius:10px;background:#fff;">
              <div style="display:flex;align-items:center;gap:10px;">
                <span style="font-size:18px;">📅</span>
                <div>
                  <div style="font-size:11px;color:#888;font-weight:500;">${ui.dateLabel}</div>
                  <div id="rest-date-display" style="font-size:15px;font-weight:700;color:#1a1a1a;">${formatDateDisplay(selectedDateStr, lang)}</div>
                </div>
              </div>
              <span id="rest-date-toggle-icon" style="color:#00B900;font-size:16px;transition:transform 0.2s;">▼</span>
            </div>
            <div id="rest-litepicker-container" class="litepicker-inline collapsed"></div>
            <input type="hidden" id="rest-date-hidden" value="${selectedDateStr}" />
          </div>
          <div id="rest-time-slots-container" style="display:flex;flex-wrap:wrap;gap:8px;">
            ${(currentMeal.slots || (currentMeal as any).timeSlots || []).map((slot: string) => `
              <button id="rest-time-chip-${slot.replace(':','_')}" class="rest-time-chip time-chip" onclick="window.selectRestTimeSlot?.('${slot}')"
                style="padding:8px 14px;border-radius:20px;border:1px solid ${selectedTimeSlot === slot ? '#00B900' : '#e0e0e0'};background:${selectedTimeSlot === slot ? '#00B900' : '#fff'};color:${selectedTimeSlot === slot ? '#fff' : '#333'};font-size:13px;font-weight:600;cursor:pointer;"
              >${slot}</button>`).join('')}
          </div>
        </div>

        <!-- Party Size & Dietary -->
        <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
          <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;">
            <div>
              <div style="font-size:15px;font-weight:600;color:#1a1a1a;">${ui.partyTitle}</div>
              <div style="font-size:12px;color:#666;">${ui.partySub}</div>
            </div>
            <div style="display:flex;align-items:center;gap:12px;border:1px solid #e0e0e0;border-radius:8px;padding:4px 8px;">
              <button onclick="window.changePartySize?.(-1)" style="border:none;background:none;font-size:18px;font-weight:700;color:#00B900;width:28px;cursor:pointer;">-</button>
              <span id="rest-party-display" style="font-size:15px;font-weight:600;min-width:20px;text-align:center;">${partySizeCount}</span>
              <button onclick="window.changePartySize?.(1)" style="border:none;background:none;font-size:18px;font-weight:700;color:#00B900;width:28px;cursor:pointer;">+</button>
            </div>
          </div>
          <div style="border-top:1px solid #f0f0f0;padding-top:12px;margin-bottom:12px;">
            <label style="display:block;font-size:13px;font-weight:600;color:#333;margin-bottom:6px;">${ui.dietaryTitle}</label>
            <input type="text" id="rest_dietary" value="${dietaryInput}" onchange="window.updateRestDietary?.(this.value)" placeholder="${ui.dietaryPlace}" style="width:100%;padding:10px 12px;border:1px solid #e0e0e0;border-radius:8px;font-size:14px;outline:none;" />
          </div>
          <div style="border-top:1px solid #f0f0f0;padding-top:12px;">
            <label style="display:block;font-size:13px;font-weight:600;color:#333;margin-bottom:6px;">${ui.roomTitle}</label>
            <input type="text" id="rest_room_number" value="${roomNumberInput}" onchange="window.updateRestRoomNumber?.(this.value)" placeholder="${ui.roomPlace}" style="width:100%;padding:10px 12px;border:1px solid #e0e0e0;border-radius:8px;font-size:14px;outline:none;" />
          </div>
        </div>

        <!-- Free Reservation Badge -->
        <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);display:flex;align-items:center;justify-content:space-between;">
          <div>
            <div style="font-size:13px;color:#666;font-weight:500;">${ui.tableFee}</div>
            <div style="font-size:11px;color:#888;">${ui.tableNote}</div>
          </div>
          <div style="font-size:18px;font-weight:700;color:#00B900;">${ui.freeBook}</div>
        </div>
      </div>
      ` : ''}
    </div>
    ${AppVersionFooter()}
    ${BackToTopButton('rest')}
    ${isReserve ? FloatingNav() : ''}
  `;
}


function scrollActiveTabToCenter(outletId: string): void {
  const scrollContainer = document.getElementById('rest-outlets-scroll');
  const tabEl = document.getElementById(`rest-outlet-tab-${outletId}`);
  if (tabEl && scrollContainer) {
    const offsetLeft = tabEl.offsetLeft - (scrollContainer.clientWidth / 2) + (tabEl.clientWidth / 2);
    scrollContainer.scrollTo({ left: Math.max(0, offsetLeft), behavior: 'smooth' });
  }
}

export function resetToFirstOutlet(): void {
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search || '') : null;
  const hasExplicit = urlParams?.get('outlet') || urlParams?.get('outletId') || urlParams?.get('table');
  if (!hasExplicit && restaurantOutlets.length > 0) {
    activeOutletId = restaurantOutlets[0].id || null;
    if (activeOutletId) loadMenuForOutlet(activeOutletId);
  }
}
if (typeof window !== 'undefined') {
  (window as any).resetRestOutlet = resetToFirstOutlet;
}

// ── Init handlers (called after DOM is ready) ────────────────────────────────
export function initReservationHandlers(): void {
  if (!restConfigLoaded) {
    getRestaurantConfig().then((cfg: any) => {
      restConfigLoaded = true;
      if (cfg) {
        const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search || '') : null;
        const explicitOutlet = urlParams ? (urlParams.get('outlet') || urlParams.get('outletId')) : null;
        const hasTableParam = Boolean(urlParams?.get('table'));
        const storedOutlet = hasTableParam
          ? (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('_guest_outlet_id') : null)
             || (typeof localStorage !== 'undefined' ? localStorage.getItem('_guest_outlet_id') : null)
          : null;
        const targetOutletId = explicitOutlet || storedOutlet;

        if (cfg.outlets?.length > 0) {
          restaurantOutlets = cfg.outlets;
          if (targetOutletId && restaurantOutlets.some((o) => o.id === targetOutletId)) {
            activeOutletId = targetOutletId;
          } else {
            // ALWAYS focus on the first restaurant outlet when opening normally!
            activeOutletId = restaurantOutlets[0].id || null;
          }
        } else if (cfg.id) {
          restaurantOutlets = [cfg];
          activeOutletId = (targetOutletId === cfg.id ? targetOutletId : null) || cfg.id || 'rest_main';
        }
        const active = restaurantOutlets.find((o) => o.id === activeOutletId) || restaurantOutlets[0];
        if (activeOutletId) {
          loadMenuForOutlet(activeOutletId);
        }
        if (active?.mealPeriods?.length) {
          MEAL_PERIODS = active.mealPeriods.map((mp: any) => ({
            id: mp.id, name: mp.name,
            slots: mp.timeSlots?.length ? mp.timeSlots : (mp.slots || ['12:00', '18:00']),
          }));
          if (!MEAL_PERIODS.some((m) => m.id === selectedMealId)) selectedMealId = MEAL_PERIODS[0].id;
          const m = MEAL_PERIODS.find((meal) => meal.id === selectedMealId);
          if (m?.slots?.length) selectedTimeSlot = m.slots[0];
        }
        (window as any).renderApp?.();
        setTimeout(() => {
          if (activeOutletId) scrollActiveTabToCenter(activeOutletId);
        }, 80);
      }
    }).catch(() => { restConfigLoaded = true; });
  }

  // Scroll active tab into view on handler init
  setTimeout(() => {
    if (activeOutletId) scrollActiveTabToCenter(activeOutletId);
  }, 50);

  const activeOutlet = getActiveOutlet();
  if ((activeOutlet.images?.length ?? 0) > 0) {
    const el = document.getElementById('rest-images-container');
    if (el) renderCarousel(el, activeOutlet.images!);
  }

  (window as any).selectRestOutlet = (id: string) => {
    if (activeOutletId === id) return;
    activeOutletId = id;
    loadMenuForOutlet(id);
    const active = restaurantOutlets.find((o) => o.id === id);
    if (active?.mealPeriods?.length) {
      MEAL_PERIODS = active.mealPeriods.map((mp: any) => ({ id: mp.id, name: mp.name, slots: mp.timeSlots?.length ? mp.timeSlots : (mp.slots || ['12:00']) }));
      if (!MEAL_PERIODS.some((m) => m.id === selectedMealId)) selectedMealId = MEAL_PERIODS[0].id;
      const m = MEAL_PERIODS.find((meal) => meal.id === selectedMealId);
      if (m?.slots?.length) selectedTimeSlot = m.slots[0];
    }
    const savedY = window.scrollY;
    (window as any).renderApp?.();
    window.scrollTo({ top: savedY, behavior: 'instant' as ScrollBehavior });
    setTimeout(() => {
      scrollActiveTabToCenter(id);
    }, 50);
  };

  (window as any).selectMealPeriod = (id: string) => {
    if (selectedMealId === id) return;
    selectedMealId = id;
    const activeMeal = MEAL_PERIODS.find((m) => m.id === id);
    if (activeMeal?.slots?.length && !activeMeal.slots.includes(selectedTimeSlot)) selectedTimeSlot = activeMeal.slots[0];
    document.querySelectorAll<HTMLButtonElement>('.rest-meal-btn').forEach((btn) => { btn.style.border = '1px solid #e0e0e0'; btn.style.background = '#fff'; btn.style.color = '#333'; btn.style.fontWeight = '400'; });
    const btn = document.getElementById(`rest-meal-btn-${id}`);
    if (btn) { btn.style.border = '1px solid #00B900'; btn.style.background = '#E8F5E8'; btn.style.color = '#00B900'; btn.style.fontWeight = '600'; }
    const tc = document.getElementById('rest-time-slots-container');
    if (tc && activeMeal) tc.innerHTML = (activeMeal.slots || []).map((slot) => `<button id="rest-time-chip-${slot.replace(':','_')}" class="rest-time-chip time-chip" onclick="window.selectRestTimeSlot?.('${slot}')" style="padding:8px 14px;border-radius:20px;border:1px solid ${selectedTimeSlot===slot?'#00B900':'#e0e0e0'};background:${selectedTimeSlot===slot?'#00B900':'#fff'};color:${selectedTimeSlot===slot?'#fff':'#333'};font-size:13px;font-weight:600;cursor:pointer;">${slot}</button>`).join('');
  };

  (window as any).selectSeatingArea = (id: string) => {
    if (selectedSeating === id) return;
    selectedSeating = id;
    document.querySelectorAll<HTMLButtonElement>('.rest-seat-btn').forEach((b) => { b.style.border='1.5px solid #e0e0e0'; b.style.background='#fff'; b.style.color='#333'; b.style.fontWeight='400'; });
    const btn = document.getElementById(`rest-seat-btn-${id.replace(/[^a-zA-Z0-9]/g,'_')}`);
    if (btn) { btn.style.border='1.5px solid #00B900'; btn.style.background='#F9FDF9'; btn.style.color='#00B900'; btn.style.fontWeight='600'; }
  };

  (window as any).selectRestTimeSlot = (slot: string) => {
    if (selectedTimeSlot === slot) return;
    selectedTimeSlot = slot;
    document.querySelectorAll<HTMLElement>('.rest-time-chip').forEach((el) => { el.style.border='1px solid #e0e0e0'; el.style.background='#fff'; el.style.color='#333'; });
    const el = document.getElementById(`rest-time-chip-${slot.replace(':','_')}`);
    if (el) { el.style.border='1px solid #00B900'; el.style.background='#00B900'; el.style.color='#fff'; }
  };

  (window as any).changePartySize = (delta: number) => {
    const n = Math.max(1, Math.min(20, partySizeCount + delta));
    if (n === partySizeCount) return;
    partySizeCount = n;
    const el = document.getElementById('rest-party-display');
    if (el) el.textContent = String(partySizeCount);
  };

  (window as any).updateRestDietary = (val: string) => { dietaryInput = val; };
  (window as any).updateRestRoomNumber = (val: string) => { roomNumberInput = val; };

  (window as any).proceedRestToGuestInfo = () => {
    const lang = getCurrentLanguage();
    dietaryInput = (document.getElementById('rest_dietary') as HTMLInputElement)?.value ?? dietaryInput;
    roomNumberInput = (document.getElementById('rest_room_number') as HTMLInputElement)?.value ?? roomNumberInput;
    const ao = getActiveOutlet();
    const seas = (ao.seatingAreas?.length ?? 0) > 0 ? ao.seatingAreas! : SEATING_AREAS;
    const meal = (ao.mealPeriods || MEAL_PERIODS).find((m: any) => m.id === selectedMealId) || MEAL_PERIODS[2];
    const seatingObj = seas.find((s) => s.id === selectedSeating) || seas[0] || SEATING_AREAS[0];
    const mealNameObj = typeof meal.name === 'object' ? meal.name : { th: meal.name, en: meal.name, zh: meal.name };
    const seatingNameObj = typeof seatingObj.name === 'object' ? seatingObj.name : { th: seatingObj.name, en: seatingObj.name, zh: seatingObj.name };
    setState({
      bookingType: 'RESTAURANT',
      restaurantDetails: {
        outletId: ao.id || 'rest-outlet', outletName: ao.name || { th: 'ร้านอาหาร', en: 'Restaurant', zh: '餐厅' },
        mealPeriodId: meal.id, mealPeriodName: mealNameObj, seatingArea: seatingObj.id, seatingAreaName: seatingNameObj,
        bookingDate: selectedDateStr, timeSlot: selectedTimeSlot, partySize: partySizeCount,
        dietaryRestrictions: dietaryInput, roomNumber: roomNumberInput,
        inHouseGuestName: roomNumberInput ? `ห้อง ${roomNumberInput}` : undefined,
      },
      selectedRoom: {
        roomTypeCode: 'RESTAURANT_TABLE', roomTypeName: getLocalizedText(ao.name, lang, 'Restaurant'),
        ratePlanCode: 'PAY_AT_VENUE', ratePlanName: 'ชำระเงินที่ร้าน (Pay at Restaurant)',
        totalRate: 0, ratePerRoom: 0, averageNightlyRate: 0, totalBeforeTax: 0,
        numberOfRooms: 1, currency: 'THB', available: 1,
        description: `${getLocalizedText(mealNameObj, lang, meal.id)} รอบ ${selectedTimeSlot} สำหรับ ${partySizeCount} ท่าน`,
      },
    });
    navigateTo('guest-info');
  };

  window.handleForward = () => (window as any).proceedRestToGuestInfo?.();
  window.handleBack = () => navigateTo('search');

  initLitepicker();
  initBackToTop('rest');
}

// ── Litepicker Init ──────────────────────────────────────────────────────────
function initLitepicker(): void {
  const container = document.getElementById('rest-litepicker-container');
  const trigger = document.getElementById('rest-date-range-trigger');
  const display = document.getElementById('rest-date-display');
  const icon = document.getElementById('rest-date-toggle-icon');
  if (!container || !trigger) return;
  const lang = getCurrentLanguage();
  const langMap: Record<string, string> = { th:'th-TH', en:'en-US', zh:'zh-CN', ru:'ru-RU', ja:'ja-JP', ko:'ko-KR' };
  trigger.addEventListener('click', () => {
    container.classList.toggle('collapsed');
    if (icon) icon.style.transform = container.classList.contains('collapsed') ? '' : 'rotate(180deg)';
  });
  new Litepicker({
    element: container, inlineMode: true, singleMode: true,
    numberOfMonths: 1, numberOfColumns: 1, firstDay: 1, minDate: new Date(),
    startDate: selectedDateStr, format: 'YYYY-MM-DD', lang: langMap[lang] || 'en-US',
    plugins: ['mobilefriendly'], mobileFriendly: true,
    setup: (picker) => {
      picker.on('selected', (start) => {
        if (!start) return;
        const iso = start.format('YYYY-MM-DD');
        selectedDateStr = iso;
        if (display) display.textContent = formatDateDisplay(iso, lang);
        const hidden = document.getElementById('rest-date-hidden') as HTMLInputElement;
        if (hidden) hidden.value = iso;
        setTimeout(() => { container.classList.add('collapsed'); if (icon) icon.style.transform = ''; }, 300);
      });
    },
  });
}

function formatDateDisplay(iso: string, lang: string): string {
  const langMap: Record<string, string> = { th:'th-TH', en:'en-US', zh:'zh-CN', ru:'ru-RU', ja:'ja-JP', ko:'ko-KR' };
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString(langMap[lang] || 'en-US', { weekday:'long', day:'numeric', month:'long', year:'numeric' });
  } catch (_) { return iso; }
}

function applyEditData(data: any, seatingAreas: SeatingArea[]): void {
  const rest = data.restaurantDetails || data.customFields?.restaurantDetails;
  if (!rest) return;
  if (rest.bookingDate) selectedDateStr = rest.bookingDate;
  if (rest.timeSlot) selectedTimeSlot = rest.timeSlot;
  if (rest.mealPeriodId) selectedMealId = rest.mealPeriodId;
  if (rest.seatingArea) { const m = matchSeatingAreaId(seatingAreas, rest.seatingArea); if (m) selectedSeating = m; }
  if (rest.partySize) partySizeCount = rest.partySize;
  if (rest.dietaryRestrictions && !dietaryInput) dietaryInput = rest.dietaryRestrictions;
  if (rest.roomNumber && !roomNumberInput) roomNumberInput = rest.roomNumber;
  if (rest.outletId && restaurantOutlets.some((o) => o.id === rest.outletId)) activeOutletId = rest.outletId;
}

function getUiText(lang: string) {
  const map: Record<string, Record<string, string>> = {
    th: { mealTitle:'🍳 เลือกมื้ออาหาร', seatingTitle:'🪑 เลือกโซนที่นั่ง', dateTitle:'📅 เลือกวันและเวลา', partyTitle:'👥 จำนวนที่นั่ง', partySub:'จำนวนผู้รับประทานอาหาร', dietaryTitle:'🥗 ข้อจำกัดด้านอาหาร (ถ้ามี)', dietaryPlace:'เช่น แพ้กุ้ง, ทานเจ', roomTitle:'🏨 หมายเลขห้อง (ถ้ามี)', roomPlace:'เช่น 1002', openHours:'🕒 เวลาทำการ', tableFee:'ค่าบริการจองโต๊ะ', tableNote:'ชำระค่าอาหารตามจริงที่ร้าน', freeBook:'จองฟรี (Free)', dateLabel:'วันที่' },
    en: { mealTitle:'🍳 Select Meal Period', seatingTitle:'🪑 Select Seating Area', dateTitle:'📅 Select Date & Time', partyTitle:'👥 Party Size', partySub:'Number of guests', dietaryTitle:'🥗 Dietary Restrictions (Optional)', dietaryPlace:'e.g. Seafood allergy, Vegan', roomTitle:'🏨 Room Number (Optional)', roomPlace:'e.g. Room 1002', openHours:'🕒 Opening Hours', tableFee:'Table Reservation', tableNote:'Pay for food at venue', freeBook:'FREE', dateLabel:'Date' },
    zh: { mealTitle:'🍳 选择用餐时间段', seatingTitle:'🪑 选择用餐区域', dateTitle:'📅 选择日期与时间', partyTitle:'👥 用餐人数', partySub:'用餐人数', dietaryTitle:'🥗 饮食忌口 (可选)', dietaryPlace:'例如: 海鲜过敏、素食', roomTitle:'🏨 房间号 (可选)', roomPlace:'例如: 1002', openHours:'🕒 营业时间', tableFee:'订座费用', tableNote:'餐饮费用到店按实际消费支付', freeBook:'免费预订', dateLabel:'日期' },
    ru: { mealTitle:'🍳 Выберите прием пищи', seatingTitle:'🪑 Выберите зону', dateTitle:'📅 Выберите дату и время', partyTitle:'👥 Количество гостей', partySub:'Количество гостей', dietaryTitle:'🥗 Пищевые ограничения (необязательно)', dietaryPlace:'напр. аллергия на морепродукты', roomTitle:'🏨 Номер комнаты (необязательно)', roomPlace:'напр. 1002', openHours:'🕒 Часы работы', tableFee:'Бронирование столика', tableNote:'Оплата за еду на месте', freeBook:'Бесплатно', dateLabel:'Дата' },
    ja: { mealTitle:'🍳 お食事の時間帯を選択', seatingTitle:'🪑 お席のエリアを選択', dateTitle:'📅 日時を選択', partyTitle:'👥 ご利用人数', partySub:'お食事される人数', dietaryTitle:'🥗 アレルギー・食事制限 (任意)', dietaryPlace:'例: 甲殻類アレルギー', roomTitle:'🏨 部屋番号 (任意)', roomPlace:'例: 1002', openHours:'🕒 営業時間', tableFee:'席の予約料', tableNote:'お食事代は現地にてお支払い', freeBook:'予約無料', dateLabel:'日付' },
    ko: { mealTitle:'🍳 식사 시간대 선택', seatingTitle:'🪑 좌석 구역 선택', dateTitle:'📅 날짜 및 시간 선택', partyTitle:'👥 예약 인원', partySub:'식사 인원', dietaryTitle:'🥗 식이 제한 (선택사항)', dietaryPlace:'예: 해산물 알레르기', roomTitle:'🏨 객실 번호 (선택사항)', roomPlace:'예: 1002호', openHours:'🕒 운영 시간', tableFee:'테이블 예약금', tableNote:'식음료 비용은 현장에서 결제', freeBook:'무료 예약', dateLabel:'날짜' },
  };
  return map[lang] || map.en;
}
